import { DeleteDialog } from '@/components/dialogs/DeleteDialog';
import { LessonFolderSharingDialog } from '@/containers/LessonFolderSharingDialog/LessonFolderSharingDialog';
import { LessonSharingDialog } from '@/containers/LessonSharingDialog/LessonSharingDialog';
import { LessonStartDialog } from '@/containers/LessonStartDialog/LessonStartDialog';
import { Class } from '@/models/class';
import { Lesson } from '@/models/lesson';
import { LessonFolder } from '@/models/lessonFolder';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { useSession } from '@/providers/session/SessionProvider';
import { Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FOLDER_PAGE_SIZE, LESSON_PAGE_SIZE } from '../../Lessons.page';
import { ContextualItem } from '../../type';
import { LessonMoveDialog } from '../actions/LessonMoveDialog';
import { LessonRenameDialog } from '../actions/LessonRenameDialog';
import { EmptyView } from './EmptyView';
import { FolderGrid } from './FolderGrid';
import { Action } from './LessonCard';
import { LessonFolderCreateDialog } from './LessonFolderCreateDialog';
import { LessonsGrid } from './LessonGrid';

export type LessonTabProps = {
    // states
    tabIndex?: number;
    lessons: Lesson[] | undefined;
    folderId?: string;
    folders: LessonFolder[] | undefined;
    selectedItems?: ContextualItem[];
    isLoadingMoreLessons: boolean;
    isLoadingMoreFolders: boolean;
    totalLessonsCount: number | undefined;
    totalFoldersCount: number;

    // callbacks
    onNavigateToFolder: (folderId: string, tabIndex: number) => void;
    onOpenEditor: (lesson: Lesson) => void;
    onUpdateFolder: (
        result: {
            name: string;
        } | null,
        selectedFolder: LessonFolder,
    ) => Promise<void>;
    onDeleteFolder: (
        confirm: boolean,
        selectedFolder: {
            name: string;
            ids: string[];
        },
    ) => Promise<void>;
    onDeleteShareFolder: (
        confirm: boolean,
        selectedFolder: {
            name: string;
            folderId: string;
            shareIds: string[];
        },
    ) => Promise<void>;
    onDeleteShareLesson: (
        confirm: boolean,
        selectedLesson: {
            name: string;
            lessonId: string;
            shareIds: string[];
        },
    ) => Promise<void>;
    onSharedFolder?: (sharedFolderId: string, users: { id: string; email: string }[]) => void;
    onStartLesson: (
        result: {
            name: string;
            class: Class;
            passlock?: string;
        } | null,
        selectedLesson: Lesson,
    ) => Promise<void>;
    onRenameLesson: (
        result: {
            name: string;
        } | null,
        selectedLesson: Lesson,
    ) => Promise<void>;
    onDuplicateLesson: (lesson: Lesson, toFolderId: string) => Promise<Lesson | null>;
    onRemoveLesson: (result: boolean, selectedLesson: Lesson) => Promise<void>;
    onMoveLesson: (value: LessonFolder | null, selectedLesson: Lesson) => Promise<void>;
    onSelectFolder: (folder: LessonFolder) => void;
    onSelectLesson: (lesson: Lesson) => void;
    onBulkRemove: (confirm: boolean) => Promise<void>;
    onBulkUnshare: (confirm: boolean) => Promise<void>;
    onBulkMoveCancel: () => void;
    onBulkMove: (value: LessonFolder | null) => Promise<void>;
    onLoadMoreLessons: () => Promise<void>;
    onLoadMoreFolders: () => Promise<void>;
    onShareLesson?: (lesson: Lesson) => void;
    onUnshareLesson?: (lesson: Lesson) => void;
};

export const LessonsTab: React.FC<LessonTabProps> = ({
    tabIndex,
    lessons,
    folderId,
    folders,
    selectedItems,
    isLoadingMoreLessons,
    isLoadingMoreFolders,
    totalLessonsCount,
    totalFoldersCount,
    onNavigateToFolder,
    onOpenEditor,
    onUpdateFolder,
    onDeleteFolder,
    onDeleteShareFolder,
    onDeleteShareLesson,
    onSharedFolder,
    onStartLesson,
    onRenameLesson,
    onDuplicateLesson,
    onRemoveLesson,
    onMoveLesson,
    onLoadMoreLessons,
    onSelectFolder,
    onSelectLesson,
    onLoadMoreFolders,
    onShareLesson,
    onUnshareLesson,
}) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { session } = useSession();

    // states
    const [updateLessonFolderDialogData, setUpdateLessonFolderDialogData] = React.useState<LessonFolder | null>();
    const [deleteFolderDialogData, setDeleteFolderDialogData] = React.useState<{ name: string; ids: string[] } | null>(null);
    const [removeAccessFromShareLessonDialogData, setRemoveAccessFromShareLessonDialogData] = React.useState<{ name: string; lessonId: string; shareIds: string[] } | null>();
    const [removeAccessFromShareFolderDialogData, setRemoveAccessFromShareFolderDialogData] = React.useState<{ name: string; folderId: string; shareIds: string[] } | null>();
    const [lessonStartDialogData, setLessonStartDialogData] = React.useState<Lesson | null>();
    const [lessonRenameDialogData, setLessonRenameDialogData] = React.useState<Lesson | null>();
    const [deleteLessonDialogData, setDeleteLessonDialogData] = React.useState<Lesson | null>();
    const [lessonSharingDialogData, setLessonSharingDialogData] = React.useState<Lesson | null>();
    const [folderSharingDialogData, setFolderSharingDialogData] = React.useState<LessonFolder | null>();
    const [lessonMoveDialogData, setLessonMoveDialogData] = React.useState<Lesson | null>(null);

    // functions
    const handleLessonAction = React.useCallback(
        async (lesson: Lesson, action: Action) => {
            switch (action) {
                case 'start':
                    setLessonStartDialogData(lesson);
                    break;
                case 'rename':
                    setLessonRenameDialogData(lesson);
                    break;
                case 'duplicate': {
                    // if not lesson owner, duplicate in root folder
                    const isOwner = lesson?.isOwnedBy(session?.user?.teacherProfile ?? { id: '' });
                    const newLesson = await onDuplicateLesson(lesson, isOwner && folderId ? folderId : '');
                    if (newLesson) {
                        // navigate to the new lesson
                        navigate(`/lessons/${newLesson.id}`, { state: { lessonId: newLesson.id, prevPath: `/lessons/${newLesson.id}` }, viewTransition: true });
                    }
                    break;
                }
                case 'remove':
                    setDeleteLessonDialogData(lesson);
                    break;
                case 'share':
                    setLessonSharingDialogData(lesson);
                    break;
                case 'move':
                    setLessonMoveDialogData(lesson);
                    break;
                case 'unshare':
                    setRemoveAccessFromShareLessonDialogData({ name: lesson.latestLessonDraft.lessonName, lessonId: lesson.id, shareIds: lesson.shares?.map((share) => share.id) ?? [] });
                    break;
            }
        },
        [folderId, onDuplicateLesson, navigate, session?.user?.teacherProfile],
    );

    const selectedFolders = React.useMemo(() => {
        return selectedItems?.filter((item) => item instanceof LessonFolder);
    }, [selectedItems]);

    const selectedLessons = React.useMemo(() => {
        return selectedItems?.filter((item) => item instanceof Lesson);
    }, [selectedItems]);

    return (
        <>
            {!folderId && (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Typography variant='h6'>{t('lesson.folder.title')}</Typography>
                    {folders && folders.length === 0 ? (
                        <EmptyView title={t('lesson.folder.empty')} />
                    ) : (
                        <>
                            <FolderGrid
                                isSelecting={selectedItems && selectedItems.length > 0}
                                selectedFolders={selectedFolders}
                                onSelected={onSelectFolder}
                                folders={folders}
                                onPreviewClick={(folder) => {
                                    onNavigateToFolder(folder.id, tabIndex ?? 0);
                                }}
                                onActionClick={(folder, action) => {
                                    switch (action) {
                                        case 'rename':
                                            setUpdateLessonFolderDialogData(folder);
                                            break;
                                        case 'remove':
                                            setDeleteFolderDialogData({ name: folder.name, ids: [folder.id] });
                                            break;
                                        case 'share':
                                            setFolderSharingDialogData(folder);
                                            break;
                                        case 'unshare':
                                            setRemoveAccessFromShareFolderDialogData({ name: folder.name, folderId: folder.id, shareIds: folder.shares?.map((share) => share.id) ?? [] });
                                            break;
                                    }
                                }}
                                loading={folders === undefined ? FOLDER_PAGE_SIZE : undefined}
                            />

                            {totalFoldersCount !== undefined && folders && folders.length < totalFoldersCount && (
                                <Stack direction='row' justifyContent='flex-start'>
                                    <Button
                                        color='secondary'
                                        onClick={() => {
                                            onLoadMoreFolders();
                                        }}
                                        loading={isLoadingMoreFolders}
                                    >
                                        {t('lesson.folder.viewMore')}
                                    </Button>
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
            )}

            <Stack spacing={1.5} sx={{ flex: 1 }}>
                <Typography variant='h6'>{t('lesson.folder.lessons')}</Typography>
                {lessons && lessons.length === 0 ? (
                    <EmptyView title={t('lesson.section.empty.title')} />
                ) : (
                    <EditorProvider>
                        <LessonsGrid
                            isSelecting={selectedItems && selectedItems.length > 0}
                            selectedLessons={selectedLessons}
                            onSelected={onSelectLesson}
                            lessons={lessons}
                            onPreviewClick={onOpenEditor}
                            onActionClick={handleLessonAction}
                            onLoadMore={onLoadMoreLessons}
                            loading={
                                lessons === undefined || isLoadingMoreLessons
                                    ? (() => {
                                          const total = totalLessonsCount ? totalLessonsCount : 0;
                                          const remaining = total - (lessons?.length ?? 0);
                                          return total === 0 ? { size: LESSON_PAGE_SIZE } : { size: remaining > LESSON_PAGE_SIZE ? LESSON_PAGE_SIZE : remaining };
                                      })()
                                    : undefined
                            }
                        />
                    </EditorProvider>
                )}
            </Stack>

            {updateLessonFolderDialogData && (
                <LessonFolderCreateDialog
                    open={true}
                    onClose={async (result) => {
                        await onUpdateFolder(result, updateLessonFolderDialogData);
                        setUpdateLessonFolderDialogData(null);
                    }}
                    payload={{ data: { folder: updateLessonFolderDialogData } }}
                />
            )}

            {deleteFolderDialogData && (
                <DeleteDialog
                    title={t('lesson.folder.actions.remove') + ` "${deleteFolderDialogData.name}"`}
                    description={t('lesson.folder.delete.description')}
                    description2={''}
                    payload={null}
                    open={!!deleteFolderDialogData}
                    onClose={async (confirm: boolean) => {
                        await onDeleteFolder(confirm, deleteFolderDialogData);
                        setDeleteFolderDialogData(null);
                    }}
                />
            )}

            {removeAccessFromShareFolderDialogData && (
                <DeleteDialog
                    title={t('lesson.folder.actions.removeAccess') + ` "${removeAccessFromShareFolderDialogData.name}"`}
                    description={t('lesson.folder.delete.removeFolderAccessDescription', { user: session?.user?.email })}
                    description2={''}
                    payload={null}
                    open={!!removeAccessFromShareFolderDialogData}
                    onClose={async (confirm: boolean) => {
                        await onDeleteShareFolder(confirm, removeAccessFromShareFolderDialogData);
                        setRemoveAccessFromShareFolderDialogData(null);
                    }}
                />
            )}

            {removeAccessFromShareLessonDialogData && (
                <DeleteDialog
                    title={t('lesson.folder.actions.removeAccess') + ` "${removeAccessFromShareLessonDialogData.name}"`}
                    description={t('lesson.folder.delete.removeLessonAccessDescription', { user: session?.user?.email })}
                    description2={''}
                    payload={null}
                    open={!!removeAccessFromShareLessonDialogData}
                    onClose={async (confirm: boolean) => {
                        await onDeleteShareLesson(confirm, removeAccessFromShareLessonDialogData);
                        setRemoveAccessFromShareLessonDialogData(null);
                    }}
                />
            )}

            {lessonStartDialogData && (
                <LessonStartDialog
                    payload={undefined}
                    open={true}
                    onClose={async (result) => {
                        await onStartLesson(result, lessonStartDialogData);
                        setLessonStartDialogData(null);
                    }}
                />
            )}

            {lessonRenameDialogData && (
                <LessonRenameDialog
                    open={true}
                    onClose={async (result) => {
                        await onRenameLesson(result, lessonRenameDialogData);
                        setLessonRenameDialogData(null);
                    }}
                    payload={{ data: { lesson: lessonRenameDialogData } }}
                />
            )}

            {deleteLessonDialogData && (
                <DeleteDialog
                    title={t('lesson.removal.title', { lessonName: deleteLessonDialogData?.latestLessonDraft.lessonName ?? '' })}
                    description={t('lesson.removal.message')}
                    description2={''}
                    payload={null}
                    open={!!deleteLessonDialogData}
                    onClose={async (result) => {
                        await onRemoveLesson(result, deleteLessonDialogData);
                        setDeleteLessonDialogData(null);
                    }}
                />
            )}

            {lessonSharingDialogData && (
                <LessonSharingDialog
                    fullWidth
                    maxWidth='sm'
                    open={true}
                    onClose={() => {
                        setLessonSharingDialogData(null);
                    }}
                    onCancel={() => {
                        setLessonSharingDialogData(null);
                    }}
                    onShared={(lesson) => {
                        onShareLesson?.(lesson);
                        setLessonSharingDialogData(null);
                    }}
                    onUnshared={(lesson) => {
                        onUnshareLesson?.(lesson);
                    }}
                    lesson={lessonSharingDialogData}
                />
            )}

            {lessonMoveDialogData && (
                <LessonMoveDialog
                    open={!!lessonMoveDialogData}
                    title={lessonMoveDialogData.latestLessonDraft.lessonName}
                    onClose={() => {
                        setLessonMoveDialogData(null);
                    }}
                    onUpdate={async (value) => {
                        await onMoveLesson(value, lessonMoveDialogData);
                    }}
                />
            )}

            {folderSharingDialogData && (
                <LessonFolderSharingDialog
                    fullWidth
                    maxWidth='sm'
                    open={true}
                    onClose={() => {
                        setFolderSharingDialogData(null);
                    }}
                    onCancel={() => {
                        setFolderSharingDialogData(null);
                    }}
                    onShared={(users) => {
                        onSharedFolder?.(folderSharingDialogData.id, users);
                        setFolderSharingDialogData(null);
                    }}
                    folder={folderSharingDialogData}
                />
            )}
        </>
    );
};
